import Phaser from "phaser";
import { FAMILY_BODY, FAMILY_HEADING, FAMILY_MONO, GAME_HEIGHT, GAME_WIDTH } from "../util/constants";
import { ensureBackdropForKey } from "../art/BackdropArt";
import { getMusic } from "../audio/Music";
import { drawPanel } from "../ui/Panel";
import { Button } from "../ui/Button";
import { battleById, resolveBattleForPath } from "../data/battles";
import type { ClassKind, ItemKind, UnitDef, WeaponKind } from "../combat/types";
import { ensureUnitTexture } from "../art/UnitArt";
import { createUnit } from "../combat/Unit";
import { ITEM_CATALOG } from "../combat/items";
import { sfxClick, sfxHover } from "../audio/Sfx";
import { SettingsButton } from "../ui/SettingsButton";
import { createScrollableText } from "../ui/scrollableText";
import { clearSuspendedBattle, getAssignedInventory, getSevenPath, loadSave, writeSave } from "../util/save";
import type { BattleId } from "../data/contentIds";

interface PrepArgs {
  battleId: BattleId;
  /** Opened from the camp's "Start Next Chapter": the back button returns there. */
  from?: "camp";
}

const classLabel = (k: ClassKind): string => {
  switch (k) {
    // Tier 1
    case "swordsman": return "Swordsman";
    case "spearton":  return "Spearton";
    case "archer":    return "Archer";
    case "knight":    return "Knight";
    case "shinobi":   return "Shinobi";
    case "sentinel":  return "Sentinel";
    case "dactyl_rider": return "Dactyl Rider";
    case "lenscaster":   return "Lenscaster";
    // Tier 2 — added when the promotion system landed.
    case "swordmaster":    return "Swordmaster";
    case "spearton_lord":  return "Spearton Lord";
    case "khan":           return "Khan";
    case "robinhelm":      return "Robinhelm";
    case "dactyl_king":    return "Dactyl King";
    case "shinobi_master": return "Shinobi Master";
    case "guardian":       return "Guardian";
    case "prismarch":      return "Prismarch";
    // Special
    case "boss": return "Adversary";
  }
};

// Set once the player has opened Inventory + Trade (or waved the pointer
// off): the first-visit pointer to it doesn't come back.
const INVENTORY_TIP_FLAG = "tip_prep_inventory_seen";

const weaponLabel = (w: WeaponKind): string => {
  switch (w) {
    case "sword": return "Sword";
    case "spear": return "Spear";
    case "shield": return "Shield";
    case "bow": return "Bow";
    case "dactyl": return "Dactyl";
    case "lens": return "Lens";
  }
};

export class BattlePrepScene extends Phaser.Scene {
  private battleId!: BattleId;
  private inventoryTip: Phaser.GameObjects.GameObject[] = [];
  constructor() { super("BattlePrepScene"); }
  private fromCamp = false;
  init(data: PrepArgs): void {
    this.battleId = data.battleId;
    this.fromCamp = data.from === "camp";
  }

  create(): void {
    const rawNode = battleById(this.battleId);
    // Per-path intro/subtitle for the endgame climaxes.
    const node = rawNode ? resolveBattleForPath(rawNode, getSevenPath(loadSave())) : undefined;
    if (!node) { this.scene.start("OverworldScene"); return; }

    const bgKey = ensureBackdropForKey(this, node.backdropKey);
    this.add.image(GAME_WIDTH / 2, GAME_HEIGHT / 2, bgKey).setDisplaySize(GAME_WIDTH, GAME_HEIGHT);
    const v = this.add.graphics();
    v.fillGradientStyle(0x000000, 0x000000, 0x000000, 0x000000, 0.55, 0.55, 0.85, 0.85);
    v.fillRect(0, 0, GAME_WIDTH, GAME_HEIGHT);

    // Title block
    this.add.text(GAME_WIDTH / 2, 50, `${node.title} — ${node.subtitle}`, {
      fontFamily: FAMILY_HEADING,
      fontSize: "30px",
      color: "#f4d999",
      stroke: "#1a0e04",
      strokeThickness: 4
    }).setOrigin(0.5);
    this.add.text(GAME_WIDTH / 2, 88, node.difficultyLabel, {
      fontFamily: FAMILY_HEADING,
      fontSize: "16px",
      color: "#c9b07a"
    }).setOrigin(0.5);

    // Intro panel — body text is scrollable so paragraph-length intros
    // (b07's monastery briefing wraps to 6+ lines) don't bleed past the
    // panel's bottom border. The scrollable region starts at y+44 (below
    // the "Field Brief" header) and runs to y+H-12 (12px bottom padding).
    const introX = 60;
    const introY = 130;
    const introW = GAME_WIDTH - 120;
    const introH = 130;
    const introG = this.add.graphics();
    drawPanel(introG, introX, introY, introW, introH);
    this.add.text(introX + 24, introY + 18, "Field Brief", {
      fontFamily: FAMILY_HEADING,
      fontSize: "16px",
      color: "#f4d999"
    });
    createScrollableText(this, {
      x: introX + 24,
      y: introY + 44,
      w: introW - 48,
      h: introH - 44 - 12,
      text: node.intro,
      style: {
        fontFamily: FAMILY_BODY,
        fontSize: "15px",
        color: "#dad3bd",
        lineSpacing: 4
      }
    });

    // Combat primer
    const primerX = 60;
    const primerY = 280;
    const primerW = 380;
    const primerH = 360;
    const primerG = this.add.graphics();
    drawPanel(primerG, primerX, primerY, primerW, primerH);
    this.add.text(primerX + 24, primerY + 18, "Tactical Reminder", {
      fontFamily: FAMILY_HEADING,
      fontSize: "16px",
      color: "#f4d999"
    });
    const primer = [
      "• Phase-based: your units act first, then enemies.",
      "• Within a phase, units act in Speed order.",
      "• Each unit spends Action Points (AP).",
      "• Move · Attack · Ready · Defend · Potion (1 AP).",
      "• Weapon triangle: Sword > Spear > Shield > Sword.",
      "• Triangle advantage = passive 1.5× counter.",
      "• Mounted units (Knight, Dactyl) get +2 move.",
      "• Items: 5 max per character; assign from squad pool.",
      "• Abilities: Boss Fighter, Aide, Destruct, Roam.",
      "• Click a unit to inspect; click active to clear."
    ];
    this.add.text(primerX + 24, primerY + 50, primer.join("\n"), {
      fontFamily: FAMILY_BODY,
      fontSize: "13px",
      color: "#dad3bd",
      lineSpacing: 6,
      wordWrap: { width: primerW - 48 }
    });

    // Roster panel
    const rosterX = 460;
    const rosterY = 280;
    const rosterW = GAME_WIDTH - rosterX - 60;
    const rosterH = 360;
    const rosterG = this.add.graphics();
    drawPanel(rosterG, rosterX, rosterY, rosterW, rosterH);
    this.add.text(rosterX + 24, rosterY + 18, "Your Vanguard", {
      fontFamily: FAMILY_HEADING,
      fontSize: "16px",
      color: "#f4d999"
    });

    const players: UnitDef[] = node.buildPlayers ? node.buildPlayers() : [];

    // Scrollable roster region — clip rows to the panel interior.
    // rowH bumped to 116 (was 80) to fit two new rows per character:
    //   row 0: Name (16px)
    //   row 1: Class · Weapon (12px)
    //   row 2: Stat line (12px mono)
    //   row 3: ABL — abilities (12px)         ← NEW
    //   row 4: INV — assigned inventory (12px) ← NEW
    const listTop = rosterY + 50;
    const listBottom = rosterY + rosterH - 16;
    const listH = listBottom - listTop;
    const listLeft = rosterX + 12;
    const listW = rosterW - 24;
    const rowH = 116;
    const contentH = players.length * rowH;
    const maxScroll = Math.max(0, contentH - listH);

    // Pre-load the save once so each row can read its assigned inventory
    // without thrashing localStorage. The InventoryScene writes to
    // assignedInventory each time the player makes a change; reopening
    // BattlePrepScene re-reads here.
    const saveSnapshot = loadSave();

    const rowsContainer = this.add.container(0, 0);
    let py = 0;
    for (const def of players) {
      const u = createUnit(def, { x: 0, y: 0 });
      const tex = ensureUnitTexture(this, u);
      // Portrait sits vertically centred on the row's top half so the
      // name + stat block can run alongside it without overlap. Vertical
      // centre at y=30 keeps it aligned with the top three text rows.
      const portrait = this.add.image(rosterX + 40, listTop + py + 30, tex).setDisplaySize(48, 60);
      const nameTxt = this.add.text(rosterX + 80, listTop + py, def.name, {
        fontFamily: FAMILY_HEADING,
        fontSize: "16px",
        color: "#f8f0d8"
      });
      const classTxt = this.add.text(rosterX + 80, listTop + py + 22, `${classLabel(def.classKind)} · ${weaponLabel(def.weapon)}`, {
        fontFamily: FAMILY_BODY,
        fontSize: "12px",
        color: "#c9b07a"
      });
      const statTxt = this.add.text(rosterX + 80, listTop + py + 40, statLine(def), {
        fontFamily: FAMILY_MONO,
        fontSize: "12px",
        color: "#9da7b8"
      });
      // Abilities row — full width below the stat block. "(none)" if the
      // unit has no special abilities so the row's vertical rhythm
      // doesn't collapse on rows where it's missing.
      const ablLabel = def.abilities && def.abilities.length > 0
        ? def.abilities.join(", ")
        : "(none)";
      const ablTxt = this.add.text(rosterX + 80, listTop + py + 60, `ABL  ${ablLabel}`, {
        fontFamily: FAMILY_MONO,
        fontSize: "12px",
        color: "#a89a78"
      });
      // Inventory row — reads the player's assigned-inventory snapshot
      // for this character. Tally by kind so "🧪 Potion ×3" reads more
      // cleanly than three identical entries. "(unassigned)" prompts
      // the player to hit the Inventory + Trade button before marching.
      const assigned = getAssignedInventory(saveSnapshot, def.id);
      let invLabel: string;
      if (assigned.length === 0) {
        invLabel = "(unassigned — visit the Inventory + Trade screen)";
      } else {
        const counts: Partial<Record<ItemKind, number>> = {};
        for (const it of assigned) counts[it.kind] = (counts[it.kind] ?? 0) + 1;
        invLabel = (Object.keys(counts) as ItemKind[])
          .map((k) => `${ITEM_CATALOG[k].glyph} ${ITEM_CATALOG[k].name}${counts[k]! > 1 ? ` ×${counts[k]}` : ""}`)
          .join("  ");
      }
      const invColor = assigned.length === 0 ? "#7a7165" : "#dad3bd";
      const invTxt = this.add.text(rosterX + 80, listTop + py + 78, `INV  ${invLabel}`, {
        fontFamily: FAMILY_MONO,
        fontSize: "12px",
        color: invColor,
        wordWrap: { width: rosterW - 100 }
      });
      rowsContainer.add([portrait, nameTxt, classTxt, statTxt, ablTxt, invTxt]);
      py += rowH;
    }

    const maskShape = this.make.graphics({ x: 0, y: 0 }, false);
    maskShape.fillStyle(0xffffff);
    maskShape.fillRect(listLeft, listTop, listW, listH);
    rowsContainer.setMask(maskShape.createGeometryMask());

    if (maxScroll > 0) {
      // Track scroll position via container y offset.
      let scroll = 0;
      const trackX = rosterX + rosterW - 12;
      const trackG = this.add.graphics();
      const drawTrack = (): void => {
        trackG.clear();
        trackG.fillStyle(0x1a0e04, 0.6);
        trackG.fillRect(trackX - 3, listTop, 6, listH);
        const thumbH = Math.max(24, (listH / contentH) * listH);
        const thumbY = listTop + (scroll / maxScroll) * (listH - thumbH);
        trackG.fillStyle(0xc9b07a, 0.85);
        trackG.fillRect(trackX - 3, thumbY, 6, thumbH);
      };
      drawTrack();

      const hitZone = this.add.zone(rosterX + rosterW / 2, listTop + listH / 2, rosterW, listH).setInteractive();
      hitZone.on("wheel", (_p: Phaser.Input.Pointer, _dx: number, dy: number) => {
        scroll = Phaser.Math.Clamp(scroll + dy * 0.5, 0, maxScroll);
        rowsContainer.y = -scroll;
        drawTrack();
      });
      this.input.on("wheel", (p: Phaser.Input.Pointer, _objs: unknown, _dx: number, dy: number) => {
        // getWorldPoint → design coords, so the region test stays correct
        // under native-res camera zoom (RENDER_SCALE > 1). No-op at scale 1.
        const wp = this.cameras.main.getWorldPoint(p.x, p.y);
        if (wp.x < rosterX || wp.x > rosterX + rosterW) return;
        if (wp.y < listTop || wp.y > listBottom) return;
        scroll = Phaser.Math.Clamp(scroll + dy * 0.5, 0, maxScroll);
        rowsContainer.y = -scroll;
        drawTrack();
      });
    }

    // Buttons
    new Button(this, {
      x: 60, y: GAME_HEIGHT - 56,
      w: 180, h: 40,
      label: this.fromCamp ? "◂ Back to Camp" : "◂ Back to Map",
      primary: false,
      fontSize: 14,
      onClick: () => this.scene.start(this.fromCamp ? "CampScene" : "OverworldScene")
    });
    // Inventory — opens the squad pool / per-character bag distribution
    // / trading post modal as a paused overlay. Sits between the map
    // and march buttons so the player naturally reaches for it after
    // reading the brief and before committing to battle.
    const invBtn = { x: GAME_WIDTH - 460, y: GAME_HEIGHT - 56, w: 200, h: 40 };
    new Button(this, {
      ...invBtn,
      label: "Inventory + Trade",
      primary: false,
      fontSize: 14,
      onClick: () => {
        sfxClick();
        this.dismissInventoryTip();
        this.scene.pause();
        this.scene.run("InventoryScene", {
          battleId: node.id,
          resumeKey: this.scene.key
        });
      }
    });
    // Mid-battle suspend: when the save holds a snapshot for THIS battle
    // (tab closed mid-fight, or the player stepped out to the map), offer
    // to pick the fight back up at the turn it was left on. Marching in
    // fresh discards the snapshot — the two buttons are an explicit
    // resume-or-restart choice.
    const suspended = loadSave().suspendedBattle;
    const canResume = suspended?.battleId === node.id;
    if (canResume && suspended) {
      new Button(this, {
        x: GAME_WIDTH - 240, y: GAME_HEIGHT - 104,
        w: 200, h: 40,
        label: `Resume — Round ${suspended.initiative.round} ▸`,
        primary: true,
        fontSize: 15,
        onClick: () => {
          sfxClick();
          this.cameras.main.fadeOut(450, 0, 0, 0);
          this.cameras.main.once("camerafadeoutcomplete", () =>
            this.scene.start("BattleScene", { battleId: node.id, resume: true })
          );
        }
      });
    }
    new Button(this, {
      x: GAME_WIDTH - 240, y: GAME_HEIGHT - 56,
      w: 200, h: 40,
      label: canResume ? "Restart Battle ▸" : "March to Battle ▸",
      primary: !canResume,
      fontSize: 16,
      onClick: () => {
        sfxClick();
        // A fresh march discards any suspended state for this battle.
        clearSuspendedBattle();
        this.cameras.main.fadeOut(450, 0, 0, 0);
        this.cameras.main.once("camerafadeoutcomplete", () => this.scene.start("BattleScene", { battleId: node.id }));
      }
    });

    // A new player's first look at this screen (battle 1 only): the
    // squad's starting potions sit unassigned in the pool, and the
    // Inventory button is easy to walk past on the way to "March to Battle".
    this.inventoryTip = [];
    if (node.id === "b01_palace_coup" && saveSnapshot.flags[INVENTORY_TIP_FLAG] !== true
      && (saveSnapshot.squadInventory?.length ?? 0) > 0) {
      this.showInventoryTip(invBtn);
    }

    getMusic(this).play(node.prepMusic, { fadeMs: 800 });
    this.cameras.main.fadeIn(450, 0, 0, 0);

    new SettingsButton(this, GAME_WIDTH - 32, 32);
  }

  /** A card over the Inventory button, an arrow at it, and a pulse round it. */
  private showInventoryTip(btn: { x: number; y: number; w: number; h: number }): void {
    const W = 380, PAD = 18;
    const cx = btn.x + btn.w / 2;
    const X = Math.min(GAME_WIDTH - W - 20, cx - W / 2);
    const body = this.add.text(0, 0,
      "Your squad's potions are waiting in the shared pool. Open Inventory + Trade and give each fighter a few before you march. Items left in the pool don't come into battle.", {
        fontFamily: FAMILY_BODY, fontSize: "15px", color: "#ece6d6", wordWrap: { width: W - PAD * 2 }, lineSpacing: 4
      });
    const H = 40 + body.height + 52;
    const Y = btn.y - 40 - H;
    body.setPosition(X + PAD, Y + 38);
    const g = this.add.graphics();
    drawPanel(g, X, Y, W, H);
    const title = this.add.text(X + PAD, Y + 13, "NEW HERE?", {
      fontFamily: FAMILY_HEADING, fontSize: "15px", color: "#f4d999", letterSpacing: 2
    });
    const got = new Button(this, {
      x: X + W - 112, y: Y + H - 42, w: 96, h: 30,
      label: "Got it", primary: true, fontSize: 13,
      onClick: () => { sfxClick(); this.dismissInventoryTip(); }
    });
    // The arrow bobs between the card and the button.
    const arrow = this.add.text(cx, btn.y - 18, "▼", {
      fontFamily: "Arial, sans-serif", fontSize: "24px", color: "#ffd45a", stroke: "#1a0e04", strokeThickness: 4
    }).setOrigin(0.5);
    this.tweens.add({ targets: arrow, y: arrow.y - 7, duration: 380, yoyo: true, repeat: -1, ease: "Sine.easeInOut" });
    // A gold outline breathing round the button itself.
    const ring = this.add.graphics();
    ring.lineStyle(2, 0xffd45a, 1);
    ring.strokeRect(btn.x - 4, btn.y - 4, btn.w + 8, btn.h + 8);
    this.tweens.add({ targets: ring, alpha: 0.25, duration: 650, yoyo: true, repeat: -1, ease: "Sine.easeInOut" });

    this.inventoryTip = [g, title, body, got, arrow, ring];
    for (const o of this.inventoryTip) {
      const v = o as unknown as Phaser.GameObjects.Components.Depth & Phaser.GameObjects.Components.Alpha;
      // The panel under everything on it (the body was made first, to size it).
      v.setDepth(o === g ? 50 : 51);
      if (o !== ring) v.setAlpha(0);
    }
    this.tweens.add({ targets: this.inventoryTip.filter((o) => o !== ring), alpha: 1, duration: 300, delay: 500 });
    this.time.delayedCall(500, () => sfxHover());
  }

  private dismissInventoryTip(): void {
    if (this.inventoryTip.length === 0) return;
    for (const o of this.inventoryTip) {
      this.tweens.killTweensOf(o);
      o.destroy();
    }
    this.inventoryTip = [];
    const s = loadSave();
    s.flags[INVENTORY_TIP_FLAG] = true;
    writeSave(s);
  }
}

const statLine = (u: UnitDef): string =>
  `HP ${u.stats.hp} · PWR ${u.stats.power} · ARM ${u.stats.armor} · SPD ${u.stats.speed} · MOV ${u.stats.movement} · AP ${u.stats.ap}`;
